import { useState } from 'react';
import { Popover } from '@base-ui/react/popover';
import { Select } from '@base-ui/react/select';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import clockSvg from '@assets/analytics/clock.svg?raw';
import chevronDownSvg from '@assets/analytics/chevron-down.svg?raw';
import { TriggerContent } from '../DatePicker/TriggerContent';
import { ClickableDay } from '../DatePicker/ClickableDay';
import type { ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './DateTimeField.module.css';

// The picker follows the same tz-naive convention as the date-only
// `DateField`: the emitted JS Date's *local* calendar fields (year, month,
// day, hour, minute) match what the user picked, regardless of the
// browser tz. The submit boundary re-anchors those fields to project tz
// via `pickerDateTimeToProjectMs` — see `@utils/dateUtils`.
//
// Don't reintroduce `timeZone: 'Asia/Tokyo'` on these formatters: doing so
// would re-interpret the Date's epoch through JST, which differs from the
// local fields whenever the browser tz isn't JST. The local fields ARE the
// canonical values; the formatters must read them verbatim.

interface IDateTimeFieldProps {
  value: Date | null;
  onChange: (next: Date) => void;
  placeholder: string;
  ariaLabel: string;
  minDate?: Date;
  maxDate?: Date;
  /** Specific calendar days to disable (e.g. days the selected entity was absent). */
  disabledDates?: Date[];
  disabled?: boolean;
}

const TIME_SLOTS: ISelectionOption[] = (() => {
  const slots: ISelectionOption[] = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 30) {
      const id = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      slots.push({ id, label: id });
    }
  }
  return slots;
})();

const dateFormatter = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// 24-hour clock to match the slot list inside the popup ("00:00", "00:30",
// ..."23:30"). Mixing 12h here against 24h slots was visually confusing —
// pick one format and use it everywhere.
const timeFormatter = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function getCurrentSlot(d: Date | null): string | null {
  if (!d) return null;
  // Read local fields directly — by convention the JS Date's local h/m
  // ARE the project-tz clock values, so no zone conversion is needed.
  const h = d.getHours();
  const m = d.getMinutes() < 30 ? 0 : 30;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Combined date + time picker matching trusstor's analysis-center
 * design: a single trigger showing "MM/DD/YYYY HH:MM am/pm" that opens
 * one popup with the calendar grid and a time-of-day trigger at the
 * bottom. Mirrors the IncidentReport card's date input.
 */
export function DateTimeField({ value, onChange, placeholder, ariaLabel, minDate, maxDate, disabledDates, disabled }: IDateTimeFieldProps) {
  const [open, setOpen] = useState(false);

  const handleDate = (next: Date | undefined) => {
    if (!next) return;
    // Preserve the calendar fields the picker emitted (already in local tz,
    // which by convention represents the project-tz calendar day). Carry
    // over the time-of-day from the previous value, or default to 00:00.
    const merged = new Date(next);
    if (value) {
      merged.setHours(value.getHours(), value.getMinutes(), 0, 0);
    } else {
      merged.setHours(0, 0, 0, 0);
    }
    onChange(merged);
  };

  const handleSlot = (slot: string) => {
    const [h, m] = slot.split(':').map(Number);
    // Set local hours/minutes — they're interpreted as project-tz clock
    // values at the submit boundary (see `pickerDateTimeToProjectMs`).
    const base = value ? new Date(value) : new Date();
    base.setHours(h!, m!, 0, 0);
    onChange(base);
  };

  const display = value
    ? `${dateFormatter.format(value)} ${timeFormatter.format(value)}`
    : null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        type="button"
        aria-label={ariaLabel}
        className={styles.trigger}
        disabled={disabled}
      >
        <TriggerContent display={display} placeholder={placeholder} />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner className={styles.positioner} sideOffset={4} align="start" collisionPadding={8}>
          <Popover.Popup className={styles.popup}>
            <DayPicker
              mode="single"
              selected={value ?? undefined}
              onSelect={handleDate}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
                ...(disabledDates ?? []),
              ]}
              showOutsideDays
              components={{ Day: ClickableDay }}
            />
            <Select.Root
              value={getCurrentSlot(value) ?? ''}
              onValueChange={(next: string | null) => { if (next) handleSlot(next); }}
              disabled={disabled || !value}
            >
              <Select.Trigger aria-label={`${ariaLabel} time`} className={styles.timeTrigger}>
                <span
                  className={styles.clockIcon}
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{ __html: clockSvg }}
                />
                <Select.Value className={styles.timeValue}>
                  {value ? timeFormatter.format(value) : <span className={styles.timePlaceholder}>--:--</span>}
                </Select.Value>
                <Select.Icon className={styles.timeChevron}>
                  <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: chevronDownSvg }} />
                </Select.Icon>
              </Select.Trigger>
              <Select.Portal>
                {/* `alignItemWithTrigger={false}` is load-bearing: the
                    Base-UI default anchors the selected item to the
                    trigger position by expanding the popup to fit every
                    item (so the popup grows as the user scrolls/the
                    Positioner re-anchors). Sibling `SingleSelect` opts
                    out for the same reason — we want a fixed-height
                    scrollable popup, not a native-style aligned menu. */}
                <Select.Positioner sideOffset={4} alignItemWithTrigger={false}>
                  <Select.Popup className={styles.timePopup}>
                    <ScrollArea>
                      {TIME_SLOTS.map((slot) => (
                        <Select.Item key={slot.id} value={slot.id} className={styles.timeItem}>
                          <Select.ItemText>{slot.label}</Select.ItemText>
                        </Select.Item>
                      ))}
                    </ScrollArea>
                  </Select.Popup>
                </Select.Positioner>
              </Select.Portal>
            </Select.Root>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
